<template>
  <div class="list-header">
    <div class="player-filtering">
      <GamInputSearch v-bind="getSearchInput" />

      <div v-if="false" class="player-filter">
        <GamButton v-bind="getFilterButton" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { BaseUserDto } from '@/core/data/user/user.interface';
import { gambits } from '@/core/gambits';
import GeolocationHelper from '@/core/helpers/geolocation.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import type { GetPlayerTableFilters } from '@/core/network/http/httpClient.interface';
import { useFilterStore } from '@/stores/filter.store';
import { useTableStore } from '@/stores/table.store';
import GamButton from '@/views/components/GamButton.vue';
import GamInputSearch from '@/views/components/GamInputSearch.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { GamInputName } from '@/views/composables/constants/components/gamInput.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { GamSidePanelId } from '@/views/composables/constants/components/gamSidePanel.constants';
import { FilterStoreAction } from '@/views/composables/constants/main/store.constants';
import type { GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamInputSearchType } from '@/views/composables/models/components/GamInput';
import { computed, nextTick, onBeforeMount } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const listId = GamListId.PLAYERS;
const tableStore = useTableStore<BaseUserDto>(listId)();
const filterStore = useFilterStore<GetPlayerTableFilters>(listId)();
const router = useRouter();
const route = useRoute();

onBeforeMount(() => {
  filterStore.setListTabItems(gambits.configService.getPlayerViewTabItems());
});

const getSearchInput = computed((): GamInputSearchType<BaseUserDto> => {
  return {
    id: GamListId.PLAYERS,
    input: {
      name: GamInputName.SEARCH,
      placeholder: 'players.general.search.placeholder',
      leftIcon: GamIconName.SEARCH,
      canClear: true,
    },
    selectFirstItem: !isMobile.value,
  };
});

const isFiltersOpen = computed((): boolean => {
  return route.query?.side_panel === GamSidePanelId.PLAYER_FILTER;
});

const getFilterButton = computed((): GamButtonType => {
  return {
    leftIcon: GamIconName.FILTER,
    isIconOnly: true,
    size: GamButtonSize.LARGE,
    variant: GamButtonVariant.TERTIARY,
    isIconWhite: true,
    onClick: async () => {
      await router.push({
        ...route,
        query: {
          ...route.query,
          side_panel: isFiltersOpen.value ? undefined : GamSidePanelId.PLAYER_FILTER,
        },
      });
    },
  };
});

filterStore.$onAction(async (action) => {
  switch (action.name) {
    case FilterStoreAction.SET_FILTERS: {
      await nextTick(() => {
        if (!filterStore.getFilters()) clearSelectedLocation();
        tableStore.loadItems({ selectFirstItem: !isMobile.value });
      });
      break;
    }
    case FilterStoreAction.SET_ACTIVE_TAB: {
      await nextTick(() => {
        tableStore.loadItems({ selectFirstItem: !isMobile.value });
      });
      break;
    }
    default:
      break;
  }
});

const clearSelectedLocation = () => {
  const tableStore = useTableStore(GamListId.PLAYERS_FILTER_LOCATION)();
  tableStore.setSelectedItem(GeolocationHelper.getMyLocationOption(), -1);
  gambits.filterService.saveSelectedTableLocation(GamListId.PLAYERS, GamListId.PLAYERS_FILTER_LOCATION);
};
</script>

<style scoped lang="scss">
.list-header {
  gap: var(--spacing-l);
  display: flex;
  flex-direction: column;
}

.player-filtering {
  display: flex;
  align-self: stretch;
  gap: var(--spacing-xs);
}
</style>
